/**
 * Per-visitor demo store (SKE-4166).
 *
 * Everything a demo visitor does lives here — never on the server. The store is
 * a single versioned, namespaced localStorage entry so progress survives a
 * refresh but stays scoped to that one browser.
 *
 * Key:     `sked.demo.approvals.v1`
 * Bumping DEMO_STORE_VERSION (and the key suffix) discards every existing
 * visitor snapshot, which is the intended migration path — there is nothing
 * here worth migrating.
 */

import type {
  DemoRawComment,
  DemoRawPost,
  DemoRawSettings,
  DemoStatusType,
} from './demoTypes'

export const DEMO_STORE_VERSION = 1
export const DEMO_STORE_KEY = `sked.demo.approvals.v${DEMO_STORE_VERSION}`

/**
 * One thing the visitor did — an append-only trail later phases can read.
 *
 * `kind` distinguishes a status change from a note left against one. It is
 * optional so snapshots written before the marketer mirror shipped still parse;
 * treat a missing value as `'status'`.
 */
export interface DemoAction {
  postId: string
  status: DemoStatusType
  at: string
  kind?: 'status' | 'comment'
  /** The visitor's note, for `kind: 'comment'` entries. */
  note?: string
}

export interface DemoStore {
  version: typeof DEMO_STORE_VERSION
  createdAt: string
  /**
   * Which upstream review link this snapshot was taken from. Rotating
   * `VITE_DEMO_REVIEW_KEY` invalidates stale visitor snapshots automatically.
   */
  source: string
  settings: DemoRawSettings
  posts: DemoRawPost[]
  /** postId -> raw comment timeline. */
  comments: Record<string, DemoRawComment[]>
  actions: DemoAction[]
}

/**
 * localStorage throws in Safari private mode and when storage is disabled, and
 * a demo that hard-fails on a privacy setting is worse than a demo that forgets
 * on refresh. Fall back to a module-level snapshot in that case.
 */
let memoryFallback: DemoStore | null = null

// Resolve the usable Storage handle once. localStorage availability can't change
// within a page session, so the probe (a setItem/removeItem round-trip to detect
// Safari private mode) must not run on every read/write. `undefined` = not yet
// probed; `null` = probed, unavailable.
let cachedStorage: Storage | null | undefined

const getStorage = (): Storage | null => {
  if (cachedStorage !== undefined) {
    return cachedStorage
  }
  try {
    const storage = globalThis.window?.localStorage
    if (!storage) {
      cachedStorage = null
      return null
    }
    // Touch it — a disabled store throws on access, not on read of the object.
    const probe = `${DEMO_STORE_KEY}.probe`
    storage.setItem(probe, '1')
    storage.removeItem(probe)
    cachedStorage = storage
    return storage
  } catch {
    cachedStorage = null
    return null
  }
}

/* -------------------------------------------------------------------------- */
/* Change notification                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The store is the demo's single source of truth, and the marketer mirror
 * (see MarketerMirror.tsx) has to re-render the moment the visitor acts. Rather
 * than poll — which would burn a timer for a store that only ever changes in
 * response to a click — writes publish to these listeners, which is exactly the
 * `subscribe` half of React's `useSyncExternalStore` contract.
 *
 * Deliberately synchronous and dependency-free: the mirror must observe the
 * SAME snapshot the approver UI just wrote, with no network and no second data
 * source.
 */
type DemoStoreListener = () => void

const listeners = new Set<DemoStoreListener>()

export const subscribeDemoStore = (
  listener: DemoStoreListener
): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const notifyListeners = (): void => {
  // Copy first: a listener that unsubscribes itself while we iterate must not
  // perturb this pass.
  for (const listener of [...listeners]) {
    listener()
  }
}

const isDemoStore = (value: unknown): value is DemoStore =>
  typeof value === 'object' &&
  value !== null &&
  (value as DemoStore).version === DEMO_STORE_VERSION &&
  Array.isArray((value as DemoStore).posts) &&
  typeof (value as DemoStore).settings === 'object'

/**
 * Read the visitor's snapshot. Returns null when absent, unreadable, written by
 * an older version, or taken from a different upstream link.
 */
export const readDemoStore = (expectedSource?: string): DemoStore | null => {
  const stored = readRaw()

  if (!stored) {
    return null
  }

  if (expectedSource !== undefined && stored.source !== expectedSource) {
    return null
  }

  return stored
}

const readRaw = (): DemoStore | null => {
  // The in-memory copy is authoritative and always current — writeDemoStore
  // keeps it in sync on every mutation — so prefer it and skip re-parsing the
  // whole store on every read (the read hot path). It is null only on a fresh
  // page load, where we parse localStorage once and cache the result below.
  // (Trade-off: mutations in another tab aren't observed until reload; the demo
  // is explicitly per-visitor/per-browser, so that's acceptable.)
  if (memoryFallback) {
    return memoryFallback
  }

  const storage = getStorage()
  if (!storage) {
    return null
  }

  try {
    const raw = storage.getItem(DEMO_STORE_KEY)
    if (!raw) {
      return null
    }
    const parsed: unknown = JSON.parse(raw)
    if (!isDemoStore(parsed)) {
      return null
    }
    // Read-through cache: subsequent reads this session skip the parse.
    memoryFallback = parsed
    return parsed
  } catch {
    // Corrupt JSON — treat as "no snapshot" and let the caller rebuild.
    return null
  }
}

/**
 * Read the snapshot with no source check, returning a value whose identity is
 * stable between writes — the `getSnapshot` half of `useSyncExternalStore`.
 *
 * Identity stability is load-bearing, not incidental: `writeDemoStore` caches
 * the new object in `memoryFallback` and every read returns that same
 * reference, so React only re-renders when the store actually changed. Parsing
 * localStorage on each call would hand back a fresh object every time and spin
 * forever.
 */
export const getDemoStoreSnapshot = (): DemoStore | null => readRaw()

export const writeDemoStore = (store: DemoStore): DemoStore => {
  memoryFallback = store

  const storage = getStorage()
  if (storage) {
    try {
      storage.setItem(DEMO_STORE_KEY, JSON.stringify(store))
    } catch {
      // Quota or privacy error — the in-memory copy above still serves this
      // page load.
    }
  }

  notifyListeners()

  return store
}

export const clearDemoStore = (): void => {
  memoryFallback = null

  const storage = getStorage()
  if (storage) {
    try {
      storage.removeItem(DEMO_STORE_KEY)
    } catch {
      // Nothing useful to do; the in-memory copy is already gone.
    }
  }

  notifyListeners()
}

/** Apply a pure update to the stored snapshot and persist the result. */
export const updateDemoStore = (
  store: DemoStore,
  update: (current: DemoStore) => DemoStore
): DemoStore => writeDemoStore(update(store))

export const createDemoStore = (input: {
  source: string
  settings: DemoRawSettings
  posts: DemoRawPost[]
  comments: Record<string, DemoRawComment[]>
}): DemoStore => ({
  version: DEMO_STORE_VERSION,
  createdAt: new Date().toISOString(),
  source: input.source,
  settings: input.settings,
  posts: input.posts,
  comments: input.comments,
  actions: [],
})
